import { useQuery } from '@tanstack/react-query';

import { ErrorState, LoadingState } from '@/components/ui/state-views';
import { getProviderCalendarSettings } from '@/config/api-helpers';

const ALL_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function ReadOnlyToggle({ enabled, label }: { enabled: boolean; label: string }) {
  return (
    <label className='flex cursor-not-allowed items-center gap-3 text-sm text-neutral-500'>
      <span className='relative inline-flex h-6 w-11 shrink-0 rounded-full bg-neutral-100'>
        <span
          className={`absolute left-0.5 top-[50%] size-5 translate-y-[-50%] rounded-full bg-white shadow ${
            enabled ? 'translate-x-5' : ''
          }`}
        />
      </span>
      {label}
    </label>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div>
      <p className='mb-1 text-sm text-neutral-600'>{label}</p>
      <div className='flex h-10 items-center rounded-lg border border-neutral-200 px-3 text-sm text-neutral-900'>
        {value ?? '\u2014'}
      </div>
    </div>
  );
}

export function CalendarTab({ providerId }: { providerId: number }) {
  const {
    data: settings,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['provider-calendar-settings', providerId],
    queryFn: () => getProviderCalendarSettings(providerId),
  });

  if (error)
    return (
      <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Calendar settings are unavailable' />
    );
  if (isLoading || !settings) return <LoadingState message='Loading settings…' />;

  const hoursByDay = new Map(settings.workingHours.map((wh) => [wh.day.charAt(0) + wh.day.slice(1).toLowerCase(), wh]));

  return (
    <div className='grid grid-cols-2 divide-x divide-neutral-100'>
      <div className='flex flex-col gap-6 p-6'>
        <div className='flex gap-4'>
          <div className='flex-1'>
            <ReadOnlyField label='Caseload goal' value={settings.caseloadGoal} />
          </div>
          <div className='flex-1'>
            <ReadOnlyField label='Max appointments per day' value={settings.maxAppointmentsPerDay} />
          </div>
        </div>

        <div>
          <p className='text-sm font-medium text-neutral-900'>Initial appointments</p>
          <p className='mt-0.5 text-xs text-neutral-500'>
            Control how patients are able to schedule new initial appointments.
          </p>
          <div className='mt-4 flex flex-col gap-4'>
            <ReadOnlyToggle enabled={settings.isAcceptingNewInitials} label='Allow all new initial appointments' />
            <ReadOnlyToggle
              enabled={settings.allowReferralInitials}
              label='Allow new initial appointments for patients with referral link'
            />
          </div>
          <div className='mt-4 flex gap-4'>
            <div className='flex-1'>
              <ReadOnlyField
                label='Min. initial appointment booking window'
                value={settings.minInitialBookingWindowDays}
              />
            </div>
            <div className='flex-1'>
              <ReadOnlyField label='Max. initial appointments per day' value={settings.maxInitialsPerDay} />
            </div>
          </div>
        </div>
      </div>

      <div className='p-6'>
        <p className='text-sm font-medium text-neutral-900'>Working hours</p>
        <p className='mt-0.5 text-xs text-neutral-500'>Weekly availability schedule.</p>
        <div className='mt-4 flex flex-col gap-4'>
          {ALL_DAYS.map((day) => {
            const wh = hoursByDay.get(day);
            return (
              <div key={day} className='flex items-center gap-3'>
                <span className='relative inline-flex h-6 w-11 shrink-0 cursor-not-allowed rounded-full bg-neutral-100'>
                  <span
                    className={`absolute left-0.5 top-[50%] size-5 translate-y-[-50%] rounded-full bg-white shadow ${
                      wh ? 'translate-x-5' : ''
                    }`}
                  />
                </span>
                <span className='w-24 text-sm text-neutral-500'>{day}</span>
                {wh && (
                  <span className='text-sm text-neutral-500'>
                    {wh.start} – {wh.end}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
